import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import getPromoCodesList from "@app/services/promoCodes/getPromoCodesList";
import patchPromoCode from "@app/services/promoCodes/patchPromoCode";

import useAuthorization from "@hooks/useAuthorization";
import { useToast } from "@hooks/useToast";

import { AdminPermissions } from "@src/enum/Permissions";

import { APPLICATION_TYPE } from "@utils/enum";
import { enumValueToTranslationKey, formatDateAndTime } from "@utils/utils";

import { INITIAL_COUPON_DATA } from "../Coupons.constants";
import { formatDiscountValue } from "../Coupons.utils";

export const useUpdateCoupon = () => {
  const { hasAllPermissions } = useAuthorization();
  const { messages } = useIntl();
  const { coupon_id: couponId } = useParams();
  const { showSuccessToast, showErrorToast } = useToast();

  const [couponDetails, setCouponDetails] = useState(() => INITIAL_COUPON_DATA);
  const [isUpdateDialogVisible, setIsUpdateDialogVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isEditPermission = hasAllPermissions([
    AdminPermissions.AdminWriteProducts,
  ]);

  useEffect(() => {
    const fetchCouponDetails = async () => {
      try {
        setIsLoading(true);

        const {
          data: [allCouponDetails],
        } = await getPromoCodesList(
          `?filter[id]=${couponId}&include=createdBy,products`
        );
        const { products = [] } = allCouponDetails;
        const updatedProducts = products.map(({ id, name }) => ({
          code: id,
          name,
        }));
        allCouponDetails.products = updatedProducts;

        setCouponDetails(allCouponDetails);
      } catch (error) {
        showErrorToast(messages.exception_error_message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCouponDetails();
  }, [couponId, messages, showErrorToast]);

  const config = {
    couponConfiguration: [
      {
        label: messages.label_coupon_code,
        value: couponDetails?.code,
      },
      {
        label: messages.label_status,
        value: couponDetails?.active
          ? messages.active
          : messages.label_inactive,
      },
      {
        label: messages.label_discount_type,
        value:
          messages[
            `label_${enumValueToTranslationKey(couponDetails?.discount_type)}`
          ],
      },
      {
        label: messages.label_discount_value,
        value: formatDiscountValue(
          couponDetails?.discount_value,
          couponDetails?.discount_type
        ),
      },
      {
        label: messages.label_valid_from,
        value: formatDateAndTime(couponDetails?.valid_from),
      },
      {
        label: messages.label_expire_date,
        value: formatDateAndTime(couponDetails?.valid_to),
      },
      {
        label: messages.label_number_of_uses,
        value: couponDetails?.max_allowed_usages,
      },
      {
        label: messages.type_label,
        value:
          messages[
            `label_${enumValueToTranslationKey(
              couponDetails?.application_type
            )}`
          ],
      },
    ],
    registration: [
      {
        label: messages.created_at,
        value: formatDateAndTime(couponDetails?.created_at),
      },
      {
        label: messages.label_created_by,
        value: couponDetails?.created_by,
      },
    ],
  };

  const handleShowUpdateForm = () => {
    setIsUpdateDialogVisible(true);
  };

  const handleCloseUpdateForm = () => {
    setIsUpdateDialogVisible(false);
  };

  const updateCoupon = async payload => {
    try {
      setIsLoading(true);

      const { data: updatedCoupon } = await patchPromoCode(couponId, payload);

      if (updatedCoupon?.created_by && !updatedCoupon?.created_by?.name) {
        updatedCoupon.created_by.name = `${updatedCoupon?.created_by?.firstname} ${updatedCoupon?.created_by?.lastname}`;
      }

      setCouponDetails(updatedCoupon);

      handleCloseUpdateForm();

      showSuccessToast(messages.message_coupon_updated);
    } catch (error) {
      showErrorToast(messages.exception_error_message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCoupon = values => {
    const { products, ...rest } = values;

    const allProducts = rest.application_type === APPLICATION_TYPE.ENTIRE_ORDER;

    const productIds = allProducts
      ? []
      : products?.map(({ id, code }) => code ?? id);

    const payload = {
      ...rest,
      products: productIds,
    };

    delete payload.created_at;
    delete payload.created_by;

    updateCoupon(payload);
  };

  const headerConfig = {
    title: messages.title_edit_coupon,
  };

  const [createdAtData, createdByData] = config.registration;

  return {
    config,
    isUpdateDialogVisible,
    isLoading,
    handleUpdateCoupon,
    handleShowUpdateForm,
    handleCloseUpdateForm,
    headerConfig,
    createdAtData,
    createdByData,
    couponDetails,
    isEditPermission,
  };
};
